import React, { useCallback, useEffect, useState } from 'react';
import { Box, Grid, Tab } from '@mui/material';
import { CheckBoxComp, ChipStatus, DialogForm, IChipsSettingProps, IDialogActions, ISnackbarAlert, MultipleSelectCheckmarks, SelectField, SnackbarAlert, TextFieldInput, TextFieldReadOnly } from '../../../shared/components';
import { GenericService } from '../../../shared/services';
import { ListId, LocalStorange } from '../../../shared/environment';
import { AccessRoleDTO, AccessRoleFilterDTO, ResumeDTO, UserDTO, UserFilterDTO, ValueListDTO } from '../../../shared/interfaces';
import { dateDiff, formatDateTime } from '../../../shared/utils';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { DynamicComboBoxPair } from '../vacancy/DynamicComboBoxPair';
import { useTranslation } from 'react-i18next';

interface IResumeMaintenanceProps {
    data: any;
    setIsOpenMaintenance: (value: boolean) => void;
    isOpenMaintenance: boolean;
    setActionExecuted: (value: boolean) => void;
}

export const ResumeMaintenance: React.FC<IResumeMaintenanceProps> = ({ data, isOpenMaintenance, setIsOpenMaintenance, setActionExecuted }) => {
    const [jobTitleList, setJobTitleList] = useState<ValueListDTO[]>([]);
    const [seniorityList, setSeniorityList] = useState<ValueListDTO[]>([]);
    const [statusList, setStatusList] = useState<ValueListDTO[]>([]);
    const [userList, setUserList] = useState<UserDTO[]>([]);

    const [resumeDTO, setResumeDTO] = useState<ResumeDTO>();

    const [loading, setLoading] = useState(false);
    const [snackBarAlert, setSnackBarAlert] = useState<ISnackbarAlert>();
    const [isOpenMessage, setIsOpenMessage] = useState(false);

    const [selectedTab, setSelectdTab] = useState('1');

    const [accessRolePermissionDTO, setAccessRolePermissionDTO] = useState<AccessRoleDTO>();
    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');
    const { t } = useTranslation();

    useEffect(() => {
        setResumeDTO({
            ...resumeDTO,
            id: '',
            name: '',
            jobTitleId: '',
            seniorityId: '',
            email: '',
            cellPhone: '',
            resume: '',
            resumeFile: '',
            statusId: ListId.talentsphere.resume.availableStatuId,
            completeAddress: '',
            linkedin: '',
            personalInformation: '',
            experienceTime: '',
            hardSkills: '',
            softSkills: '',
            professionalExperience: '',
            professionalExperiencePerHardSkill: '',
            validationQuestions: '',
            validationQuestionsAndAnswers: '',
            salaryExpectationIndividual: '',
            salaryExpectationCompany: '',
            companyId: userInfo.user.companyId
        } as ResumeDTO);

        if(isOpenMaintenance){
            handleListGetAll(ListId.talentsphere.resume.jobTitleId, setJobTitleList);
            handleListGetAll(ListId.talentsphere.resume.seniorityId, setSeniorityList);
            handleListGetAll(ListId.talentsphere.resume.statusId, setStatusList);
            handleUserGetAllAsync(setUserList);
            handleGetResumeByIdAsync();

            handleGetPermission(ListId.talentsphere.resumeId, setAccessRolePermissionDTO);

        }
    }, [data, isOpenMaintenance]);

    const handleGetPermission = useCallback(async (featureId: string, setList: any) => {
        const accessRoleFilterDTO: AccessRoleFilterDTO = {
            featureId: featureId,
            userId: userInfo.user.id,
        };
        GenericService.PostGenericAsync1('authsphere.AccessRole', 'GetPermissionAsync', accessRoleFilterDTO, setActionExecuted, setLoading, undefined, setList,);
    }, []);
    
    const handleListGetAll = useCallback(async (listId: string, setList: any) => {
        const valueListFilterDTO = {
            value: '',
            listId: listId,
            orderBy: 'value ASC'
        };
        GenericService.GetAllAsync1('parmsphere.valueList', valueListFilterDTO, setLoading, setList, showMessage);
    }, []);

    const handleUserGetAllAsync = useCallback(async (setUserList: any) => {
        const userFilterDTO: UserFilterDTO = {
            companyId: '',
            peopleId: '',
            userName: '',
            isServiceAccount: false
        };
        GenericService.GetAllViewAsync1('authsphere.user', userFilterDTO, setActionExecuted, setLoading, setUserList, showMessage, false);

    }, []);

    const handleGetResumeByIdAsync = useCallback(async () => {
        const resumeId = data.resumeId ?? data.id;
        GenericService.GetByIdAsync1('talentsphere.resume', resumeId, setLoading, setResumeDTO, showMessage);
    }, [data]);

    const handleSaveClick = useCallback(async () => {
        const dto = resumeDTO as ResumeDTO;

        if (resumeDTO?.id === '' || resumeDTO?.id === undefined) {
            dto.createdById = userInfo.user.id;
            GenericService.AddAsync1('talentsphere.resume', dto, setResumeDTO, setActionExecuted, setLoading, showMessage);
        }
        else {
            if (!accessRolePermissionDTO?.allowUpdate) {
                showMessage('There is no permission for this access.', 'info');
                return;
            }
            if(dto.isBlackList) {
                dto.statusId = ListId.talentsphere.resume.unvailableStatusId;
            }
            else {
                dto.statusId = ListId.talentsphere.resume.availableStatuId;
            }
            dto.updatedById = userInfo.user.id;
            GenericService.UpdateAsync1('talentsphere.resume', dto, setActionExecuted, setLoading, showMessage);
        }
    }, [resumeDTO]);

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {

        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarAlert(msg);
        setIsOpenMessage(true);
    }

    const chipStatus: IChipsSettingProps[] = [
        { value: t('resume.chipStatus.available'), iconName: 'person_add_alt_outlined', iconColor: 'success' },
        { value: t('resume.chipStatus.unavailable'), iconName: 'person_add_disabled_outlined', iconColor: 'error' },
        { value: t('resume.chipStatus.hired'), iconName: 'person_outlined', iconColor: 'primary' },
        { value: t('resume.chipStatus.competingForTheVacancy'), iconName: 'manage_accounts_outlined', iconColor: 'info' },
        { value: t('resume.chipStatus.deletionRequested'), iconName: 'manage_accounts_outlined', iconColor: 'warning' },
    ];
    
    const actions: IDialogActions[] = [
        { id: 'save', text: t('resume.maintenanceButton.save'), icon: 'save_outlined', iconColor: 'primary', setAction: handleSaveClick },
        { id: 'close', text: t('resume.maintenanceButton.close'), icon: 'close_outlined', iconColor: 'secondary', setAction: () => void undefined },
    ];

    const handleChange = (event: React.SyntheticEvent, newValue: string) => {
        setSelectdTab(newValue);
    };

    return (
        <React.Fragment>
            {isOpenMessage && (
                <SnackbarAlert
                    snackBarAlert={snackBarAlert}
                    setIsOpen={setIsOpenMessage}
                    isOpen={isOpenMessage}
                />
            )}
            <DialogForm
                title={t('resume.detailsName')}
                icon='article_outlined'
                setIsOpen={setIsOpenMaintenance}
                isOpen={isOpenMaintenance}
                fullWidth={true}
                maxWidth={'xl'}
                actions={actions}
                loading={loading}
            >
                <React.Fragment>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={12}>
                            <TabContext value={selectedTab} >
                                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                    <TabList onChange={handleChange} aria-label="lab API tabs" variant="scrollable" scrollButtons="auto">
                                        <Tab label={t('resume.tabItem.details')} value="1" />
                                        <Tab label={t('resume.tabItem.resume')} value="2" />
                                        <Tab label={t('resume.tabItem.language')} value="3" />
                                        <Tab label={t('resume.tabItem.hardSkill')} value="4" />
                                        <Tab label={t('resume.tabItem.softSkill')} value="5" />
                                        <Tab label={t('resume.tabItem.professionalExperience')} value="6" />
                                        <Tab label={t('resume.tabItem.courseAndCertificates')} value="7" />
                                        <Tab label={t('resume.tabItem.validationQuestions')} value="8" />
                                    </TabList>
                                </Box>
                                <TabPanel value="1" sx={{ ml: -3, mr: -3, mb: -3 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={5}>
                                            <TextFieldInput
                                                id='name'
                                                label={t('resume.maintenance.name')}
                                                placeholder={t('resume.maintenance.name')}
                                                value={resumeDTO?.name}
                                                setValue={setResumeDTO}
                                                obj={resumeDTO}
                                                required={true}
                                                maxLength={200}
                                                autoFocus={true}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <SelectField
                                                id='jobTitleId'
                                                label={t('resume.maintenance.jobTitle')}
                                                placeholder={t('resume.maintenance.jobTitle')}
                                                value={resumeDTO?.jobTitleId}
                                                setValue={setResumeDTO}
                                                obj={resumeDTO}
                                                valueList={jobTitleList}
                                                required={true}
                                                hasNoneSelect={false}
                                                searchable={true}
                                                labelKey='value'
                                                valueKey='id'
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <SelectField
                                                id='seniorityId'
                                                label={t('resume.maintenance.seniority')}
                                                placeholder={t('resume.maintenance.seniority')}
                                                value={resumeDTO?.seniorityId}
                                                setValue={setResumeDTO}
                                                obj={resumeDTO}
                                                valueList={seniorityList}
                                                required={true}
                                                hasNoneSelect={false}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <TextFieldInput
                                                id='code'
                                                label={t('resume.maintenance.code')}
                                                placeholder={t('resume.maintenance.code')}
                                                value={resumeDTO?.code}
                                                setValue={setResumeDTO}
                                                obj={resumeDTO}
                                                readonly={true}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={5}>
                                            <TextFieldInput
                                                id='email'
                                                label={t('resume.maintenance.email')}
                                                placeholder={t('resume.maintenance.email')}
                                                value={resumeDTO?.email}
                                                setValue={setResumeDTO}
                                                obj={resumeDTO}
                                                maxLength={400}
                                                type='email'
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <TextFieldReadOnly
                                                id={'statusId'}
                                                label={t('resume.maintenance.status')}
                                                value={(<ChipStatus label='' value={statusList.find(x => x.id == resumeDTO?.statusId)?.value ?? ''} chipSettings={chipStatus} />)}
                                                hasDivider={false}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <TextFieldInput
                                                id='cellPhone'
                                                label={t('resume.maintenance.cellPhone')}
                                                placeholder={t('resume.maintenance.cellPhone')}
                                                value={resumeDTO?.cellPhone}
                                                setValue={setResumeDTO}
                                                obj={resumeDTO}
                                                maxLength={20}
                                                numericOnly={true}
                                                mask="+00 (00) 00000-0000"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <TextFieldInput
                                                id='experienceTime'
                                                label={t('resume.maintenance.experienceTime')}
                                                placeholder={t('resume.maintenance.experienceTime')}
                                                value={resumeDTO?.experienceTime}
                                                setValue={setResumeDTO}
                                                obj={resumeDTO}
                                                required={true}
                                                maxLength={200}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={8}>
                                            <TextFieldInput
                                                id='completeAddress'
                                                label={t('resume.maintenance.completeAddress')}
                                                placeholder={t('resume.maintenance.completeAddress')}
                                                value={resumeDTO?.completeAddress}
                                                setValue={setResumeDTO}
                                                obj={resumeDTO}
                                                required={true}
                                                maxLength={200}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <TextFieldInput
                                                id='salaryExpectationCompany'
                                                label={t('resume.maintenance.expectedSalaryPJ')}
                                                placeholder={t('resume.maintenance.expectedSalaryPJ')}
                                                value={resumeDTO?.salaryExpectationCompany}
                                                setValue={setResumeDTO}
                                                obj={resumeDTO}
                                                maxLength={20}
                                                numericOnly={true}
                                                valueType='currency'
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <TextFieldInput
                                                id='salaryExpectationIndividual'
                                                label={t('resume.maintenance.expectedSalaryPF')}
                                                placeholder={t('resume.maintenance.expectedSalaryPF')}
                                                value={resumeDTO?.salaryExpectationIndividual}
                                                setValue={setResumeDTO}
                                                obj={resumeDTO}
                                                numericOnly={true}
                                                valueType='currency'
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <TextFieldInput
                                                id='linkedin'
                                                label={t('resume.maintenance.linkedin')}
                                                placeholder={t('resume.maintenance.linkedin')}
                                                value={resumeDTO?.linkedin}
                                                setValue={setResumeDTO}
                                                obj={resumeDTO}
                                                required={false}
                                                maxLength={200}
                                            />
                                        </Grid>
                                        {/* <Grid item xs={12}>
                                            <MultipleSelectCheckmarks
                                                id='statusListId'
                                                label='Status List'
                                                placeholder='Status List'
                                                value={resumeDTO?.statusListId ?? []}
                                                setValue={setResumeDTO}
                                                obj={resumeDTO}
                                                valueList={statusList}
                                                required={true}
                                                hasNoneSelect={false}
                                            />
                                        </Grid> */}
                                        <Grid item xs={12} md={12}>
                                            <TextFieldInput
                                                id='personalInformation'
                                                label={t('resume.maintenance.personalInformation')}
                                                placeholder={t('resume.maintenance.personalInformation')}
                                                value={resumeDTO?.personalInformation}
                                                setValue={setResumeDTO}
                                                obj={resumeDTO}
                                                required={false}
                                                multiline={true}
                                                rows={6}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <CheckBoxComp
                                                id='isIncomplete'
                                                label={t('resume.maintenance.isIncomplete')}
                                                setValue={setResumeDTO}
                                                objDTO={resumeDTO}
                                                defaultChecked={resumeDTO?.isIncomplete}
                                                disabled={false}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <CheckBoxComp
                                                id='isSuspicious'
                                                label={t('resume.maintenance.isSuspicious')}
                                                setValue={setResumeDTO}
                                                objDTO={resumeDTO}
                                                defaultChecked={resumeDTO?.isSuspicious}
                                                disabled={false}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <CheckBoxComp
                                                id='isBlackList'
                                                label={t('resume.maintenance.isBlackList')}
                                                setValue={setResumeDTO}
                                                objDTO={resumeDTO}
                                                defaultChecked={resumeDTO?.isBlackList}
                                                disabled={false}
                                            />
                                        </Grid>
                                    </Grid>
                                </TabPanel>
                                <TabPanel value="2" sx={{ ml: -3, mr: -3, mb: -3 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={12}>
                                            <TextFieldInput
                                                id='resume'
                                                label={t('resume.maintenance.resume')}
                                                value={resumeDTO?.resume}
                                                setValue={setResumeDTO}
                                                obj={resumeDTO}
                                                required={true}
                                                multiline={true}
                                                rows={16}
                                            />
                                        </Grid>
                                    </Grid>
                                </TabPanel>
                                <TabPanel value="3" sx={{ ml: -3, mr: -3, mb: -3 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={12}>
                                            <DynamicComboBoxPair
                                                title={t('resume.maintenance.languageSkill')}
                                                ownerId={resumeDTO?.id ?? data.id}
                                                skillType='language'
                                                isMandatory={true}
                                                hasValidated={true}
                                            />
                                        </Grid>
                                    </Grid>
                                </TabPanel>
                                <TabPanel value="4" sx={{ ml: -3, mr: -3, mb: -3 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={12}>
                                            <DynamicComboBoxPair
                                                title={t('resume.maintenance.hardSkill')}
                                                ownerId={data.id}
                                                skillType='hardSkill'
                                                isMandatory={true}
                                                hasValidated={true}
                                            />
                                        </Grid>
                                    </Grid>
                                </TabPanel>
                                <TabPanel value="5" sx={{ ml: -3, mr: -3, mb: -3 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={12}>
                                            <DynamicComboBoxPair
                                                title={t('resume.maintenance.softSkill')}
                                                ownerId={data.id}
                                                skillType='softSkill'
                                                isMandatory={true}
                                                hasValidated={true}
                                            />
                                        </Grid>
                                    </Grid>
                                </TabPanel>
                                <TabPanel value="6" sx={{ ml: -3, mr: -3, mb: -3 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={12}>
                                            <TextFieldInput
                                                id='professionalExperience'
                                                label={t('resume.maintenance.professionalExperience')}
                                                value={resumeDTO?.professionalExperience}
                                                setValue={setResumeDTO}
                                                obj={resumeDTO}
                                                required={false}
                                                multiline={true}
                                                rows={16}
                                            />
                                        </Grid>
                                    </Grid>
                                </TabPanel>
                                <TabPanel value="7" sx={{ ml: -3, mr: -3, mb: -3 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={12}>
                                            <TextFieldInput
                                                id='coursesAndCertificates'
                                                label={t('resume.maintenance.courseAndCertificates')}
                                                value={resumeDTO?.coursesAndCertificates}
                                                setValue={setResumeDTO}
                                                obj={resumeDTO}
                                                required={false}
                                                multiline={true}
                                                rows={16}
                                            />
                                        </Grid>
                                    </Grid>
                                </TabPanel>
                                <TabPanel value="8" sx={{ ml: -3, mr: -3, mb: -3 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={12}>
                                            <TextFieldInput
                                                id='validationQuestions'
                                                label={t('resume.maintenance.validationQuestions')}
                                                value={resumeDTO?.validationQuestions}
                                                setValue={setResumeDTO}
                                                obj={resumeDTO}
                                                required={false}
                                                multiline={true}
                                                rows={7}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <TextFieldInput
                                                id='validationQuestionsAndAnswers'
                                                label={t('resume.maintenance.validationQuestionsAndAnswers')}
                                                value={resumeDTO?.validationQuestionsAndAnswers}
                                                setValue={setResumeDTO}
                                                obj={resumeDTO}
                                                required={false}
                                                multiline={true}
                                                rows={8}
                                            />
                                        </Grid>
                                    </Grid>
                                </TabPanel>
                            </TabContext>
                        </Grid>
                        {resumeDTO?.id ? (
                            <React.Fragment>
                                <Grid item xs={12} md={4}>
                                    <TextFieldReadOnly
                                        id={'id'}
                                        label='Id'
                                        value={resumeDTO.id}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'createdById'}
                                        label={t('resume.maintenance.createdBy')}
                                        value={userList.find(x => x.id === resumeDTO.createdById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'creationDateTime'}
                                        label={t('resume.maintenance.creationDateTime')}
                                        value={formatDateTime(String(resumeDTO.creationDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updatedById'}
                                        label={t('resume.maintenance.updatedBy')}
                                        value={userList.find(x => x.id === resumeDTO.updatedById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updateDateTime'}
                                        label={t('resume.maintenance.updateDateTime')}
                                        value={formatDateTime(String(resumeDTO.updateDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                            </React.Fragment>
                        ) : undefined}
                    </Grid>
                </React.Fragment>
            </DialogForm >
        </React.Fragment >
    );
};